import type { AuthWorkspace, User, UserRole, WorkspaceTokenClaims } from '@/types';

const UI_ROLES = new Set<UserRole>([
  'admin',
  'manager',
  'operator',
  'viewer',
  'user',
  'guest',
  'sales_manager',
  'production',
  'accountant',
  'executive',
  'partner',
  'partner_admin',
  'partner_user',
  'partner_accountant',
]);

function decodeBase64Url(value: string): string {
  const normalized = value.replaceAll('-', '+').replaceAll('_', '/');
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    '='
  );
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function decodeWorkspaceTokenClaims(
  accessToken: string
): WorkspaceTokenClaims | null {
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) return null;
    const parsed = JSON.parse(decodeBase64Url(payload)) as WorkspaceTokenClaims;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function workspaceTokenMatches(
  accessToken: string,
  workspace: AuthWorkspace
): boolean {
  const claims = decodeWorkspaceTokenClaims(accessToken);
  return Boolean(
    claims &&
      claims.workspace_id === workspace.id &&
      claims.workspace_kind === workspace.kind
  );
}

function isUserRole(value: unknown): value is UserRole {
  return typeof value === 'string' && UI_ROLES.has(value as UserRole);
}

export function applyWorkspaceTokenClaims(
  user: User,
  accessToken: string,
  workspace: AuthWorkspace | null
): User {
  const claims = decodeWorkspaceTokenClaims(accessToken);
  if (!claims || !workspace || !workspaceTokenMatches(accessToken, workspace)) {
    return user;
  }

  const permissions = Array.isArray(claims.permissions)
    ? claims.permissions.filter(
        (permission): permission is string => typeof permission === 'string'
      )
    : [];

  if (workspace?.kind === 'personal_finance') {
    return {
      ...user,
      role: 'accountant',
      roles: ['accountant'],
      permissions,
    };
  }

  const tokenRoles = Array.isArray(claims.roles)
    ? claims.roles.filter(isUserRole)
    : [];
  const primaryRole = tokenRoles[0] ?? (isUserRole(claims.role) ? claims.role : user.role);
  return {
    ...user,
    role: primaryRole,
    roles: tokenRoles.length > 0 ? tokenRoles : user.roles,
    permissions,
  };
}

export function workspaceLandingPath(
  workspace: AuthWorkspace | null,
  user: User | null,
  fallback = '/v2/dashboard'
): string {
  if (workspace?.kind === 'personal_finance') {
    return '/v2/accounting/personal-1c';
  }
  const role = user?.roles?.[0] || user?.role;
  const routes: Partial<Record<UserRole, string>> = {
    admin: '/v2/dashboard',
    manager: '/v2/dashboard',
    operator: '/v2/production',
    viewer: '/v2/executive',
    user: '/v2/dashboard',
    guest: '/v2/dashboard',
    sales_manager: '/v2/dashboard',
    production: '/v2/production',
    accountant: '/v2/accounting',
    executive: '/v2/executive',
    partner: '/v2/partner-sheet',
    partner_admin: '/v2/partner-sheet',
    partner_user: '/v2/partner-sheet',
    partner_accountant: '/v2/partner-sheet',
  };
  return (role && routes[role]) || fallback;
}
